/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  HeaderMessageKey,
  getIsHeaderMessageClosed,
  setIsHeaderMessageClosed,
} from '@fai/friendli-suite/features/common/storage/header-message';
import { useContainerTeamPlan } from '@fai/friendli-suite/features/products/container/plan/use-container-team-plan';
import HeaderMessage from '@fai/friendli-suite/views/layout/header/items/header-message';

import GlobalHeaderMessage from './global';

const THRESHOLD_FOR_FREE_TRIAL_EXPIRY = 7;

function PlanTrialMessage({ daysLeft }: { daysLeft: number }) {
  const { t } = useTranslation();

  const isContainerFreeTrialAboutToExpire = daysLeft <= THRESHOLD_FOR_FREE_TRIAL_EXPIRY;

  const headerMessageKey = HeaderMessageKey.ContainerTrial;

  const onClose = () => {
    setIsHeaderMessageClosed(headerMessageKey, true);
  };

  if (getIsHeaderMessageClosed(headerMessageKey)) return null;

  return (
    <HeaderMessage
      contentButton={
        <HeaderMessage.ContentActionButton handleOnClick={() => window.Intercom('showNewMessage')}>
          {t('container.layout.contact_us')}
        </HeaderMessage.ContentActionButton>
      }
      isCloseable={!isContainerFreeTrialAboutToExpire}
      variant={isContainerFreeTrialAboutToExpire ? 'WARNING' : 'INFO'}
      onClose={onClose}
    >
      {isContainerFreeTrialAboutToExpire ? (
        <Trans i18nKey="container.layout.your_trial_ends_in" values={{ planExpiry: daysLeft }} />
      ) : (
        t('container.layout.currently_on_a_free_trial')
      )}
    </HeaderMessage>
  );
}

export default function ContainerHeaderMessage() {
  const { teamId = '' } = useParams();

  const {
    plan: containerPlan,
    daysLeft: containerDaysLeft,
    loading: planLoading,
  } = useContainerTeamPlan(teamId);

  if (planLoading) return null;

  // If plan is ENTERPRISE, don't show message.
  if (containerPlan === 'ENTERPRISE') {
    return null;
  }

  // If plan is TRIAL, only show message about trial.
  if (containerPlan === 'TRIAL') {
    return <PlanTrialMessage daysLeft={containerDaysLeft} />;
  }

  return <GlobalHeaderMessage />;
}

ContainerHeaderMessage.displayName = 'ContainerHeaderMessage';
